import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";
import { SessionMonitor } from "@/components/SessionMonitor";
import { useSecurityLogger } from "@/hooks/useSecurityLogger";
import { supabase } from "@/integrations/supabase/client";

interface AdminRouteProps {
  children: React.ReactNode;
}

export function AdminRoute({ children }: AdminRouteProps) {
  const { isAdmin, isLoading } = useUserRole();
  const navigate = useNavigate();
  const [shouldRender, setShouldRender] = useState(true);
  const redirectTimeout = useRef<NodeJS.Timeout>();
  const { logSecurityEvent } = useSecurityLogger();
  const hasLoggedRef = useRef(false);

  useEffect(() => {
    // Log unauthorized access attempt
    const logUnauthorizedAttempt = async () => {
      if (!isLoading && !isAdmin && !hasLoggedRef.current) {
        hasLoggedRef.current = true;
        const { data: { user } } = await supabase.auth.getUser();
        
        await logSecurityEvent({
          event_type: "admin_access_attempt",
          description: `Unauthorized admin area access attempt by ${user?.email || 'unauthenticated user'}`,
          severity: "critical",
          user_id: user?.id,
          was_blocked: true,
          details: {
            attempted_route: window.location.pathname,
            user_email: user?.email,
            timestamp: new Date().toISOString(),
          },
        });
      }
    };

    // Add a small delay before redirecting to prevent flicker on temporary issues
    if (!isLoading && !isAdmin) {
      logUnauthorizedAttempt();
      redirectTimeout.current = setTimeout(() => {
        setShouldRender(false);
        navigate("/dashboard");
      }, 300);
    } else {
      setShouldRender(true);
      if (redirectTimeout.current) {
        clearTimeout(redirectTimeout.current);
      }
    }

    return () => {
      if (redirectTimeout.current) {
        clearTimeout(redirectTimeout.current);
      }
    };
  }, [isAdmin, isLoading, navigate, logSecurityEvent]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin || !shouldRender) {
    return null;
  }

  return (
    <>
      <SessionMonitor isAdminRoute={true} />
      {children}
    </>
  );
}
